import { useReducer } from "react";
import biApi from "../../../../../../api/biApi";
import type { SmartViewWidget } from "../../../../../../api/types/dashboardsTypes.ts";
import type { TabularReportConfigurationForRunning } from "../../../../../../api/types/svTypes.ts";
import DrillDownEntry from "../../../../reporting/sv/components/builder/common/drillDown/DrillDownEntry.tsx";
import useViewReportHandlers from "../../../../reporting/sv/components/viewer/common/hooks/useViewReportHandlers.ts";
import { initializeTabularRunState, reducer } from "../../../../reporting/sv/components/viewer/storage/runState.ts";
import { isTabularRunState } from "../../../../reporting/sv/components/viewer/storage/runStateUtils.ts";
import { BiCommonDataContextProvider } from "../../../../reporting/sv/contexts/BiCommonDataContextProvider.tsx";
import type { DrillDownArgs } from "../DrillDownArgs.ts";
import SmartViewWidgetWrapper from "../SmartViewWidgetWrapper";
import SmartViewTabularWidgetViewer from "./SmartViewTabularWidgetViewer";

interface Props {
  widget: SmartViewWidget;
  data: TabularReportConfigurationForRunning;
  onDrillDown: (args: DrillDownArgs) => void;
}

const SmartViewTabularContainer = ({ data, widget, onDrillDown }: Props) => {
  const [runState, dispatchRunState] = useReducer(reducer, initializeTabularRunState(data, widget.reportId));

  const { handleConditionUpdate, handleFieldUpdate, handleRefreshGrid, handleSortingUpdate } =
    useViewReportHandlers(dispatchRunState);

  if (!isTabularRunState(runState)) {
    return <></>;
  }

  return (
    <BiCommonDataContextProvider
      reportType={runState.reportType}
      books={runState.books}
      isMultiBook={runState.isMultiBook}
      dataSetId={runState.dataSetId}
      dimensionsStructure={undefined}
      dimensions={runState.dimensions}
      editMode={false}
      conditions={runState.conditions}
      reportId={widget.reportId}
      refreshToken={runState.refreshToken}
      dimensionNames={runState.dimensionNames}
      measureNames={runState.measureNames}
      onConditionUpdate={handleConditionUpdate}
      onRefreshGrid={handleRefreshGrid}
      onFieldWithCustomConditionUpdate={handleFieldUpdate}
    >
      <SmartViewWidgetWrapper widget={widget} conditions={data.conditions}>
        <SmartViewTabularWidgetViewer
          runState={runState}
          runReport={biApi.dashboardReports.runTabularReport}
          onDrillDown={onDrillDown}
          onSortingUpdate={handleSortingUpdate}
        />
      </SmartViewWidgetWrapper>
      <DrillDownEntry reportId={widget.reportId} />
    </BiCommonDataContextProvider>
  );
};

export default SmartViewTabularContainer;
